import React from "react";
import { Routes as RouterRoutes, Route } from "react-router-dom";
import Login from "../auth/Login";
import VAlert from "../layout/Alert";
import { BaseRoute } from "./PrivateRoute";
import Dashboard from "../dashboard/Dashboard";
import PermissionDenied from "./PermissionDenied";
import { ProgrammesScreen } from "../programme/screens/ProgrammesScreen";
import { LayoutWithNav, LayoutWithoutNav } from "../layout/BaseLayout";
import { CreateOrganisationPage } from "../../pages/createOrganisationPage";
import { ComponentLibrary } from "../layout/ComponentLibrary";
import { UsersScreen } from "../users/UsersScreen";
import { SessionModeScreen } from "../session/sessionMode/SessionModeScreen";
import { PrepareSession } from "../session/prepareSession/PrepareSession";
import { AuthenticationLayout } from "../layout/auth/AuthenticationLayout";
import { ResetPassword, SetPassword } from "../auth/SetPassword";
import { Forgot } from "../auth/Forgot";
import { EditSessionScreen } from "../session/components/EditSessionPage";
import { StoriesScreen } from "../curation/stories/screens/StoriesScreen";
import { CollectionsScreen } from "../curation/collections/screens/CollectionsScreen";
import { CurriculumsScreen } from "../curation/curriculums/CurriculumsScreen";
import { ThemesScreen } from "../curation/themes/ThemesScreen";
import { BranchedStoriesScreen } from "../curation/branched-stories/screens/BranchedStoriesScreen";
import { BranchedStoryDetailsScreen } from "../curation/branched-stories/screens/BranchedStoryDetailsScreen";
import { BranchedStoryStructureScreen } from "../curation/branched-stories/screens/BranchedStoryStructureScreen";
import { CreateBranchedStoryWizard } from "../curation/branched-stories/wizard/CreateBranchedStoryWizard";
import { EditBranchedStoryWizard } from "../curation/branched-stories/wizard/EditBranchedStoryWizard";
import { StatsProvider } from "../stats/stats";
import { MapStory } from "../curation/stories/map/MapStory";
import { FetchProvider } from "../../core/components/crud/contexts/fetch.context";
import { getMapped } from "../../client/api/story";
import { ViewCollection } from "../home/ViewCollection";
import { ViewCollectionStory } from "../home/ViewCollectionStory";
import { ViewCollectionTheme } from "../home/ViewCollectionTheme";
import { ViewCollectionTopic } from "../home/ViewCollectionTopic";
import { ViewCurriculum } from "../home/ViewCurriculum";
//import { HomeScreen } from "../home/HomeScreen";
import { ViewOnMobile } from "../layout/ViewOnMobile";
import { AddUserScreen } from "../users/AddUserScreen";
import { ClassroomScreen } from "../classroom/ClassroomScreen";
import { useAuth } from "../../contexts/auth.context";
import { ClassSessionScreen } from "../classroom/session/ClassSessionScreen";
import { ExploreScreen } from "../home/ExploreScreen";
import SubscriptionSuccess from "../organisation/onboarding/SubscriptionSuccess";
import { PublicLayout } from "../layout/public/PublicLayout";

const Routes = (): React.ReactElement => {
    const { user } = useAuth();
    return (
        <>
            <VAlert />
            <RouterRoutes>
                {process.env.NODE_ENV === "development" && (
                    <Route
                        path="/component-library"
                        element={<BaseRoute layout={LayoutWithNav} component={ComponentLibrary} />}
                    />
                )}
                <Route
                    path="/subscription/success"
                    element={<BaseRoute layout={PublicLayout} component={SubscriptionSuccess} />}
                />
                <Route
                    path="/explore"
                    element={<BaseRoute layout={LayoutWithNav} component={ExploreScreen} permission="DashboardView" />}
                />
                <Route
                    path="/explore/curriculum/:id"
                    element={<BaseRoute layout={LayoutWithNav} component={ViewCurriculum} permission="DashboardView" />}
                />
                <Route
                    path="/explore/curriculum/:id/collection/:collectionId"
                    element={<BaseRoute layout={LayoutWithNav} component={ViewCollection} permission="DashboardView" />}
                />
                <Route
                    path="/explore/collection/:id/story/:storyId"
                    element={
                        <BaseRoute layout={LayoutWithNav} component={ViewCollectionStory} permission="DashboardView" />
                    }
                />
                <Route
                    path="/explore/collection/:id/theme/:themeId"
                    element={
                        <BaseRoute layout={LayoutWithNav} component={ViewCollectionTheme} permission="DashboardView" />
                    }
                />
                <Route
                    path="/explore/collection/:id/topic/:topicId"
                    element={
                        <BaseRoute layout={LayoutWithNav} component={ViewCollectionTopic} permission="DashboardView" />
                    }
                />
                <Route
                    path="/programmes/*"
                    element={
                        <BaseRoute layout={LayoutWithNav} component={ProgrammesScreen} permission="ProgrammeViewAll" />
                    }
                />
                <Route
                    path="/home"
                    element={<BaseRoute layout={LayoutWithNav} component={Dashboard} permission="DashboardView" />}
                />
                <Route
                    path="/add-users"
                    element={
                        <BaseRoute layout={LayoutWithNav} component={AddUserScreen} permission="FacilitatorViewAll" />
                    }
                />
                <Route
                    path="/users/*"
                    element={<BaseRoute layout={LayoutWithNav} component={UsersScreen} permission="SettingsView" />}
                />
                <Route path="/403" element={<BaseRoute layout={LayoutWithNav} component={PermissionDenied} />} />
                <Route path="/login" element={<BaseRoute layout={AuthenticationLayout} component={Login} />} />
                <Route
                    path="/create-account/:token"
                    element={<BaseRoute layout={AuthenticationLayout} component={SetPassword} />}
                />
                <Route path="/forgot" element={<BaseRoute layout={AuthenticationLayout} component={Forgot} />} />
                <Route
                    path="/reset/:token"
                    element={<BaseRoute layout={AuthenticationLayout} component={ResetPassword} />}
                />
                <Route
                    path="/organisation/signup"
                    element={<BaseRoute layout={AuthenticationLayout} component={CreateOrganisationPage} />}
                />
                <Route
                    path="/start-session/:id"
                    element={
                        <BaseRoute
                            layout={LayoutWithoutNav}
                            component={SessionModeScreen}
                            permission="ProgrammeViewAll"
                        />
                    }
                />
                <Route
                    path="/classrooms/start-session/:id"
                    element={
                        <BaseRoute
                            layout={LayoutWithoutNav}
                            component={ClassSessionScreen}
                            permission={"ClassroomView"}
                        />
                    }
                />
                <Route
                    path="/prepare-session/:id"
                    element={
                        <BaseRoute layout={LayoutWithNav} component={PrepareSession} permission="ProgrammeViewAll" />
                    }
                />
                <Route
                    path="/sessions/edit/:id"
                    element={
                        <BaseRoute layout={LayoutWithNav} component={EditSessionScreen} permission="SessionUpdate" />
                    }
                />
                <Route
                    path="/stories/*"
                    element={<BaseRoute component={StoriesScreen} layout={LayoutWithNav} permission="StoryViewAll" />}
                />

                {/* Branched Stories Routes */}
                <Route
                    path="/curation/branched-stories/*"
                    element={
                        <BaseRoute component={BranchedStoriesScreen} layout={LayoutWithNav} permission="StoryViewAll" />
                    }
                />
                <Route
                    path="/curation/branched-stories/create"
                    element={
                        <BaseRoute
                            component={CreateBranchedStoryWizard}
                            layout={LayoutWithNav}
                            permission="StoryCreate"
                        />
                    }
                />
                <Route
                    path="/curation/branched-stories/add"
                    element={
                        <BaseRoute
                            component={CreateBranchedStoryWizard}
                            layout={LayoutWithNav}
                            permission="StoryCreate"
                        />
                    }
                />
                <Route
                    path="/curation/branched-stories/:id/details"
                    element={
                        <BaseRoute
                            component={BranchedStoryDetailsScreen}
                            layout={LayoutWithNav}
                            permission="StoryView"
                        />
                    }
                />
                <Route
                    path="/curation/branched-stories/:id/structure"
                    element={
                        <BaseRoute
                            component={BranchedStoryStructureScreen}
                            layout={LayoutWithNav}
                            permission="StoryView"
                        />
                    }
                />
                <Route
                    path="/curation/branched-stories/view/:id"
                    element={
                        <BaseRoute
                            component={BranchedStoryDetailsScreen}
                            layout={LayoutWithNav}
                            permission="StoryView"
                        />
                    }
                />
                <Route
                    path="/curation/branched-stories/edit/:id"
                    element={
                        <BaseRoute
                            component={EditBranchedStoryWizard}
                            layout={LayoutWithNav}
                            permission="StoryUpdate"
                        />
                    }
                />

                <Route
                    path="/collections/*"
                    element={
                        <BaseRoute
                            component={CollectionsScreen}
                            layout={LayoutWithNav}
                            permission="StoryCollectionView"
                        />
                    }
                />
                <Route
                    path="/curriculums/*"
                    element={
                        <BaseRoute
                            component={CurriculumsScreen}
                            layout={LayoutWithNav}
                            permission="StoryCollectionCreate"
                        />
                    }
                />
                <Route
                    path="/themes/*"
                    element={
                        <BaseRoute component={ThemesScreen} layout={LayoutWithNav} permission="StoryCollectionCreate" />
                    }
                />
                <Route
                    path="/detect-mobile"
                    element={<BaseRoute layout={LayoutWithoutNav} component={ViewOnMobile} />}
                />
                <Route
                    path="/classrooms/*"
                    element={
                        <BaseRoute
                            component={ClassroomScreen}
                            layout={LayoutWithNav}
                            permission={user?.isSchool ? "ClassroomView" : undefined}
                        />
                    }
                />
                <Route
                    path="/stats/*"
                    element={<BaseRoute component={StatsProvider} layout={LayoutWithNav} permission="StatsViewAll" />}
                />
                {/** Keep this route last as routes after the fetch provider don't seem to work**/}
                <Route
                    path="/map/story/:id"
                    element={
                        <FetchProvider noun="Story" getItem={getMapped}>
                            <BaseRoute component={MapStory} layout={LayoutWithNav} permission="StoryCollectionView" />
                        </FetchProvider>
                    }
                />
            </RouterRoutes>
        </>
    );
};

export default Routes;
